/**
 * `AccountResponse.type` → the design's three groups.
 *
 * `type` is a **free string with no enum** — Plaid passthrough for linked
 * accounts, and for manual accounts whatever this client sends, unvalidated
 * by the backend (M5 spec §Grouping). The web client owns this vocabulary,
 * which is why this is a lookup object rather than a switch chain: a type
 * this table has no entry for falls to `other` rather than being guessed at,
 * because guessing can put an account on the wrong side of the
 * assets/liabilities split silently — a wrong number, not a missing one.
 */
export type Group = 'cash' | 'credit' | 'investments' | 'other'

/** `loan` groups under CREDIT — the design puts a mortgage there, not under
 *  its own heading. */
const GROUPS: Record<string, Group> = {
  depository: 'cash',
  credit: 'credit',
  loan: 'credit',
  investment: 'investments',
  brokerage: 'investments',
}

/**
 * Whether the BACKEND treats this `type` as a credit card — the only kind that
 * may carry a statement day. Mirrors `statement_cycle.py:21-31`: lower-case,
 * strip spaces (U+0020 only, not tabs), `_` and `-`, then exactly `credit` or `creditcard`. `subtype`
 * never decides.
 *
 * Deliberately NOT `groupOf(type) === 'credit'`: that group also holds `loan`
 * and is case-sensitive, so a picker on a loan would 422
 * `statement_day_not_credit`.
 */
const CREDIT_CARD_TYPES: ReadonlySet<string> = new Set(['credit', 'creditcard'])

export function isCreditCard(type: string): boolean {
  return CREDIT_CARD_TYPES.has(type.toLowerCase().replace(/[ _-]/g, ''))
}

export function groupOf(type: string): Group {
  return GROUPS[type] ?? 'other'
}

/** Display order — the design's three headings, `other` last as a catch-all
 *  for a type this client's vocabulary does not yet recognise. Moved here
 *  from `Accounts.tsx` (M5 Ticket 1) so `AccountsSummary.tsx`'s tile
 *  sub-lines and `Accounts.tsx`'s group headings read the SAME order and
 *  titles rather than two copies that can drift. */
export const GROUP_ORDER: { group: Group; title: string }[] = [
  { group: 'cash', title: 'Cash' },
  { group: 'credit', title: 'Credit' },
  { group: 'investments', title: 'Investments' },
  { group: 'other', title: 'Other' },
]

/**
 * `AccountsSummary.tsx`'s Assets/Liabilities tile sub-lines — "Cash ·
 * Investments", say — DERIVED from the groups an account with a real balance
 * actually falls into, never a hard-coded string (M5 Ticket 1's own
 * condition: "Cash + investments" is wrong the moment a household has no
 * investments). `GROUP_ORDER` decides both the membership test (`isLiability`
 * matching `liability`) and the display order/label, so a group added there
 * automatically gets a correct sub-line on both sides of the split rather
 * than a second table someone has to remember to update alongside it.
 *
 * `undefined` when nothing in that half has a balance — an all-cash household
 * has no liability groups, and the Liabilities tile should show no sub-line
 * rather than an empty one.
 */
export function accountGroupsLabel(
  accounts: { type: string; currentCents: number | null }[],
  liability: boolean,
): string | undefined {
  const present = GROUP_ORDER.filter(
    ({ group }) =>
      isLiability(group) === liability &&
      accounts.some((a) => a.currentCents !== null && groupOf(a.type) === group),
  ).map(({ title }) => title)
  return present.length > 0 ? present.join(' · ') : undefined
}

/** Only `credit` renders a negative-owed balance and a utilisation meter. */
export function isLiability(group: Group): boolean {
  return group === 'credit'
}

/**
 * The type options `ManualAccountForm`'s selector offers — the backend
 * validates nothing on `type`, so a typo written by hand there is permanent
 * bad data. One list, read by both the form and its own test
 * (`accountGroups.test.ts`), so a value added here that disagrees with
 * `GROUPS` above fails immediately rather than shipping a silently
 * misclassified account.
 */
export const MANUAL_ACCOUNT_TYPES: { value: string; label: string; group: Group }[] = [
  { value: 'depository', label: 'Chequing or savings', group: 'cash' },
  { value: 'credit', label: 'Credit card', group: 'credit' },
  { value: 'loan', label: 'Loan or mortgage', group: 'credit' },
  { value: 'investment', label: 'Investment', group: 'investments' },
  { value: 'other', label: 'Other', group: 'other' },
]

/**
 * The detail screen's "Account type" row (design gap-fill, 2026-09-17) —
 * reuses `MANUAL_ACCOUNT_TYPES` rather than a second, parallel label switch,
 * per its own docblock's warning against a second table someone has to
 * remember to update alongside it.
 *
 * **Not a complete mapping of every wire `type`.** `MANUAL_ACCOUNT_TYPES` is
 * the five options `ManualAccountForm`'s OWN picker offers — a linked
 * (Plaid) account can carry `'brokerage'`, which `GROUPS` above maps to
 * `'investments'` but which is not one of the five manual-creation options
 * at all. For a type with no exact entry, this falls back to the GROUP's
 * own title (`GROUP_ORDER`, already the vocabulary this screen's group
 * headings use) rather than inventing a new label for a raw wire string —
 * "Investments" for `'brokerage'`, not a guessed noun.
 */
export function accountTypeLabel(type: string): string {
  const exact = MANUAL_ACCOUNT_TYPES.find((t) => t.value === type)
  if (exact) return exact.label
  // `GROUP_ORDER` has exactly one entry per `Group`, so this is structurally
  // guaranteed to match — `?? 'Other'` documents that rather than asserting
  // it away with `!`, matching `groupOf`'s own "fall to a real value, never
  // throw on an unrecognised type" discipline.
  return GROUP_ORDER.find((g) => g.group === groupOf(type))?.title ?? 'Other'
}
